import { currencyFractionDigits } from '../../shared/utils/commercial-money';
import { AccountingDirectoryControlsComponent } from '../shared/accounting-directory-controls.component';
import * as XLSX from 'xlsx';
import { Component, OnInit, inject } from '@angular/core';
import { forkJoin } from 'rxjs';
import { MenuItem, MessageService } from 'primeng/api';
import { AccountsService } from "../accounts.service";
import { SessionStorageService } from "../../services/session-storage.service";
import { AccountPayables } from "./payables.model";
import { AccountPayablesLine } from "./payables-line.model";
import { Bill } from './generated-bills/bill.model';
import { TableLazyLoadEvent, TableModule } from "primeng/table";
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { MultiSelectModule } from 'primeng/multiselect';
import { DatePickerModule } from 'primeng/datepicker';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { TooltipModule } from 'primeng/tooltip';
import { ToastModule } from "primeng/toast";
import { ProgressBarModule } from "primeng/progressbar";
import { PanelModule } from "primeng/panel";
import { ImageModule } from "primeng/image";
import { ChipModule } from "primeng/chip";
import { TagModule } from "primeng/tag";
import { InputGroupModule } from "primeng/inputgroup";
import { InputGroupAddonModule } from "primeng/inputgroupaddon";
import { IftaLabelModule } from "primeng/iftalabel";
import { DrawerModule } from "primeng/drawer";
import { PermissionService } from '../../security/permission.service';
import { app_permissions } from '../../security/app-permissions';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageMetricStripComponent } from '../../shared/page-layout/page-metric-strip.component';
import { PageSearchPanelComponent } from '../../shared/page-layout/page-search-panel.component';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageMetric } from '../../shared/page-layout/page-layout.models';
import { CompanyScopeSelection, CompanyScopeSelectorComponent } from '../company-scope-selector/company-scope-selector.component';
import { CRM_PAYABLE_ROLES, CrmPartyContextService } from '../../shared/crm-client-contact-picker/crm-party-context.service';
import { LocaleService } from '../../i18n/locale.service';
import { distinctSourceReferences } from '../source-references';
import { OrdersService } from '../../order/order.service';


@Component({
    selector: 'app-payables',
    templateUrl: './payables.component.html',
    styleUrls: ['../finance-list.shared.css', './payables.component.css'],
    standalone: true,
    imports: [
    CommonModule,
    FormsModule,
    BreadcrumbModule,
    TableModule,
    MultiSelectModule,
    DatePickerModule,
    InputTextModule,
    ButtonModule,
    CardModule,
    TooltipModule,
    ToastModule,
    ProgressBarModule,
    PanelModule,
    ImageModule,
    ChipModule,
    TagModule,
    InputGroupModule,
    InputGroupAddonModule,
    IftaLabelModule,
    DrawerModule,
    PageShellComponent,
    PageHeroComponent,
    PageMetricStripComponent,
    PageSearchPanelComponent,
    PageDataPanelComponent,
    AccountingDirectoryControlsComponent,
        CompanyScopeSelectorComponent
],
    providers: [
        MessageService,
        AccountsService,
        SessionStorageService
    ]
})
export class PayablesComponent implements OnInit {
    readonly pendingStatuses = ['NEW', 'DRAFT', 'READY', 'CAPTURED', 'MATCHING', 'MATCH_EXCEPTION', 'APPROVAL_REQUIRED', 'APPROVED', 'ON_HOLD'];
    searchText = '';
    directoryMode: 'LIST' | 'CRM' | 'PARTY_TYPE' = 'LIST';
    directoryFilters: Record<string, string | number | boolean | number[] | string[] | null | undefined> = {};
    private listRequestId = 0;

    private readonly localeService = inject(LocaleService);
    private readonly ordersService = inject(OrdersService);
    billSelectionBlocker = '';
    checkingBillSelection = false;
    private billSelectionRequest = 0;
    isLoading = false;

    breadcrumbItems: MenuItem[] = [];
    homeBreadcrumb: MenuItem = {};

    // Data properties for the unified table
    allPayables: AccountPayables[] = [];
    selectedPayables: AccountPayables[] = [];

    get canGenerateSelectedBill(): boolean {
        return this.selectedPayables.length > 0 && !this.checkingBillSelection && !this.billSelectionBlocker;
    }

    onPayableSelectionChange(selection: AccountPayables[]): void {
        this.selectedPayables = selection;
        const request = ++this.billSelectionRequest;
        this.billSelectionBlocker = '';
        this.checkingBillSelection = false;
        if (this.selectedPayables.some(item => item.billId || item.billNumber || item.status === 'BILLED')) {
            this.billSelectionBlocker = 'A selected payable is already linked to a bill.';
            return;
        }
        if (this.selectedPayables.some(item => item.sourceResourceType?.toUpperCase() === 'PURCHASE_ORDER'
            && !item.sourceResourceId)) {
            this.billSelectionBlocker = 'A purchase-order payable has no source order.';
            return;
        }
        const orderItems = this.selectedPayables.filter(item =>
            item.sourceResourceType?.toUpperCase() === 'PURCHASE_ORDER' && item.sourceResourceId);
        if (!orderItems.length) return;
        this.checkingBillSelection = true;
        forkJoin(orderItems.map(item => this.ordersService.getPurchaseOrderVendorBillReadiness(item.sourceResourceId!))).subscribe({
            next: results => {
                if (request !== this.billSelectionRequest) return;
                for (let index = 0; index < results.length; index++) {
                    const readiness = results[index];
                    if (readiness.payableId !== orderItems[index].payableId) {
                        this.billSelectionBlocker = 'This purchase order has missing or duplicate payables.';
                        break;
                    }
                    const orderBlocker = readiness.blockingReasons.find(reason =>
                        !reason.toLowerCase().includes('cannot be refreshed'));
                    if (orderBlocker) {
                        this.billSelectionBlocker = orderBlocker;
                        break;
                    }
                }
                this.checkingBillSelection = false;
            },
            error: () => {
                if (request !== this.billSelectionRequest) return;
                this.billSelectionBlocker = 'Unable to verify the selected purchase order. Refresh and try again.';
                this.checkingBillSelection = false;
            }
        });
    }
    totalRecords: number = 0;
    first: number = 0;
    rows: number = 20;

    // Filter properties
    clients: any[] = [];
    selectedClients: any[] = [];
    contacts: any[] = [];
    selectedContacts: any[] = [];
    dateRange: (Date | null)[] | null = null;
    statuses: string[] = [
        'READY',
        'CAPTURED',
        'MATCHING',
        'MATCH_EXCEPTION',
        'APPROVAL_REQUIRED',
        'APPROVED',
        'DRAFT',
        'BILLED',
        'ON_HOLD',
        'CANCELLED',
        'REVERSED',
        'NEW'
    ];
    selectedStatuses: string[] = this.pendingStatuses.slice();
    sourceTypes: string[] = ['SYSTEM', 'MANUAL'];
    selectedSourceTypes: string[] = [];
    savedFilterViews = [
        { label: 'Ready to bill', statuses: ['READY', 'CAPTURED', 'APPROVED'] },
        { label: 'Needs matching', statuses: ['MATCHING'] },
        { label: 'Manual payables', statuses: [], sourceTypes: ['MANUAL'] },
        { label: 'On hold', statuses: ['ON_HOLD'] },
        { label: 'Match exceptions', statuses: ['MATCH_EXCEPTION'] },
        { label: 'Already billed', statuses: ['BILLED'] },
    ];
    referenceFilter: string | null = null;
    sourceReferenceFilter: string | null = null;
    billNumberFilter: string | null = null;

    // Summary Card properties
    totalPendingAmount: number = 0;
    totalBilledAmount: number = 0;
    reportCurrencyCode: string = 'USD'; // Default, will be updated based on company settings

    canViewPayables: boolean = false;
    canGenerateBill: boolean = false;
    canEditPayables: boolean = false;
    canExportPayables: boolean = false;
    previewPayable: AccountPayables | null = null;
    isPreviewVisible = false;
    currentScope: CompanyScopeSelection | null = null;
    createdBillDrafts: Bill[] = [];

    get isConsolidatedReadOnly(): boolean {
        return !!this.currentScope?.readOnly;
    }

    constructor(private accountsService: AccountsService,
        private messageService: MessageService,
        private router: Router,
        private sessionStorageService: SessionStorageService,
        private permissionService: PermissionService,
        public crmPartyContext: CrmPartyContextService) {
    }


    ngOnInit(): void {
        this.canViewPayables = this.permissionService.hasPermission(app_permissions.accounts.bill.view);
        this.canGenerateBill = this.permissionService.hasPermission(app_permissions.accounts.bill.create);
        this.canEditPayables = this.permissionService.hasPermission(app_permissions.accounts.bill.edit);
        this.canExportPayables = this.permissionService.hasPermission(app_permissions.accounts.bill.view);
        this.breadcrumbItems = [{ label: 'Accounts' }, { label: 'Enterprise' }, { label: 'Payables' }];
        this.homeBreadcrumb = { icon: 'pi pi-home', routerLink: '/' };
        if (!this.canViewPayables) {
            this.isLoading = false;
            return;
        }
        this.loadInitialData();
    }

    onScopeChange(scope: CompanyScopeSelection) {
        this.currentScope = scope;
        this.selectedPayables = [];
        this.applyFilters();
    }

    loadInitialData() {
        this.isLoading = true;
        const companyId = this.sessionStorageService.getCompany()?.id;
        if (!companyId) {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Company not found.' });
            this.isLoading = false;
            return;
        }

        this.crmPartyContext.loadClients(companyId, CRM_PAYABLE_ROLES).subscribe(clients => {
            this.clients = clients.map(client => ({ ...client, name: this.crmPartyContext.clientName(client) }));
        });

        // Initial data load for the table and summary is triggered by applyFilters
        this.applyFilters();
    }

    loadData(event: TableLazyLoadEvent) {
        this.isLoading = true;
        const requestId = ++this.listRequestId;
        this.first = event.first ?? 0;
        this.rows = event.rows ?? 20;

        const sessionCompanyId = this.sessionStorageService.getCompany()?.id;
        const companyGroupId = this.currentScope?.companyGroupId;
        const companyId = companyGroupId ? undefined : (this.currentScope?.companyId ?? sessionCompanyId);
        if (!companyId && !companyGroupId) return;

        const clientIds = this.selectedClients.map(c => Number(c.id?.entityId ?? c.id)).filter(Boolean);
        const contactIds = this.selectedContacts.map(c => Number(c.id)).filter(Boolean);
        const startDate = this.dateRange?.[0] ? this.dateRange[0].toISOString() : undefined;
        const endDate = this.dateRange?.[1] ? this.dateRange[1].toISOString() : undefined;

        this.directoryFilters = { owningCompanyId: companyId, companyGroupId, clientIds, crmClientContactIds: contactIds, startDate, endDate, referenceNumber: this.referenceFilter, billNumber: this.billNumberFilter, sourceTypes: this.selectedSourceTypes, sourceReference: this.sourceReferenceFilter, searchText: this.searchText };

        this.accountsService.getAccountPayables(
            this.first / this.rows, this.rows, companyId, clientIds, this.selectedStatuses,
            startDate, endDate, this.referenceFilter ?? undefined, this.billNumberFilter ?? undefined,
            this.selectedSourceTypes, this.selectedStatuses, undefined, undefined, undefined, companyGroupId, contactIds,
            this.sourceReferenceFilter?.trim() || undefined, this.searchText
        ).subscribe({
            next: (dataWrapper) => {
                if (requestId !== this.listRequestId) return;
                this.allPayables = dataWrapper.items;
                this.totalRecords = dataWrapper.count;
                this.isLoading = false;
            },
            error: (err) => {
                if (requestId !== this.listRequestId) return;
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Could not load payables.' });
                this.isLoading = false;
            }
        });

        this.accountsService.getClientPayablesSummary(companyId, clientIds, this.selectedStatuses, startDate, endDate, this.referenceFilter ?? undefined, this.billNumberFilter ?? undefined, companyGroupId)
            .subscribe(summary => {
                if (requestId !== this.listRequestId) return;
                this.totalPendingAmount = summary.pendingAmount;
                this.totalBilledAmount = summary.billedAmount;
                this.reportCurrencyCode = summary.currencyCode;
            });
    }

    onDirectoryModeChange(mode: 'LIST' | 'CRM' | 'PARTY_TYPE'): void {
        this.directoryMode = mode;
        this.selectedPayables = [];
    }

    onDirectorySearch(value: string): void {
        this.searchText = value;
        this.selectedPayables = [];
        this.applyFilters();
    }

    showPendingStatuses(): void {
        this.selectedStatuses = this.pendingStatuses.slice();
        this.applyFilters();
    }

    showAllStatuses(): void {
        this.selectedStatuses = [];
        this.applyFilters();
    }

    applyFilters() {
        // Reset to the first page when applying filters
        this.loadData({ first: 0, rows: this.rows });
    }

    clearFilters() {
        this.selectedClients = [];
        this.contacts = [];
        this.selectedContacts = [];
        this.dateRange = null;
        this.selectedStatuses = this.pendingStatuses.slice();
        this.selectedSourceTypes = [];
        this.referenceFilter = null;
        this.sourceReferenceFilter = null;
        this.billNumberFilter = null;
        this.searchText = '';
        this.applyFilters();
    }

    get metrics(): PageMetric[] {
        return [
            { label: 'Ready amount', value: this.formatMoney(this.amountByStatus(['READY', 'CAPTURED', 'APPROVED'])), hint: 'Current page ready to bill', tone: 'success' },
            { label: 'Selected amount', value: this.formatMoney(this.getSelectedTotal()), hint: `${this.selectedPayables.length} selected`, tone: 'info' },
            { label: 'Match exceptions', value: this.formatMoney(this.amountByStatus(['MATCH_EXCEPTION'])), hint: 'Current page exceptions', tone: 'danger' },
            { label: 'On hold amount', value: this.formatMoney(this.amountByStatus(['ON_HOLD'])), hint: 'Current page blocked', tone: 'warn' },
            { label: 'Approval required', value: this.formatMoney(this.amountByStatus(['APPROVAL_REQUIRED'])), hint: 'Current page approval queue', tone: 'warn' }
        ];
    }

    get activeFilterCount(): number {
        return [
            this.selectedClients.length,
            this.selectedContacts.length,
            this.dateRange?.some(Boolean) ? 1 : 0,
            this.selectedStatuses.length,
            this.selectedSourceTypes.length,
            this.referenceFilter ? 1 : 0,
            this.sourceReferenceFilter?.trim() ? 1 : 0,
            this.billNumberFilter ? 1 : 0
        ].reduce((total, value) => total + Number(value), 0);
    }

    get resultText(): string {
        return `${this.totalRecords} payables`;
    }

    onSelectedClientsChange(): void {
        this.selectedContacts = [];
        this.contacts = [];
        const companyId = this.sessionStorageService.getCompany()?.id;
        const clientIds = this.selectedClients.map(client => Number(client.id)).filter(Boolean);
        if (!clientIds.length) return;
        forkJoin(clientIds.map(clientId => this.crmPartyContext.loadContacts(clientId, companyId))).subscribe(contactGroups => {
            const seen = new Set<number>();
            this.contacts = contactGroups
                .flat()
                .filter(contact => {
                    if (!contact.id || seen.has(contact.id)) return false;
                    seen.add(contact.id);
                    return true;
                })
                .map(contact => ({ ...contact, displayName: this.crmPartyContext.contactName(contact) }));
        });
    }

    getSelectedTotal(): number {
        return this.selectedPayables.reduce((total, payable) => total + (payable.amount || 0), 0);
    }

    get expectedBillDraftCount(): number {
        return new Set(this.selectedPayables.map(payable => this.draftGroupKey(payable))).size;
    }

    get generateBillLabel(): string {
        const count = this.expectedBillDraftCount;
        return count ? `Create ${count} bill draft${count === 1 ? '' : 's'}` : 'Create bill draft';
    }

    amountByStatus(statuses: string[]): number {
        return this.allPayables
            .filter(payable => statuses.includes(payable.status || ''))
            .reduce((total, payable) => total + (payable.amount || 0), 0);
    }

    formatMoney(amount: number | undefined, currencyCode: string = this.reportCurrencyCode): string {
        return `${currencyCode || ''} ${Number(amount || 0).toLocaleString(undefined, { minimumFractionDigits: currencyFractionDigits(currencyCode || 'SAR'), maximumFractionDigits: currencyFractionDigits(currencyCode || 'SAR') })}`;
    }

    displayDocumentReference(value?: string): string {
        if (!value) return 'No reference';
        if (/^AP\/\d{4}\/\d{2}\/\d{8}$/.test(value)) return value;
        const operationalNumber = value.match(/(?:SHP|DLR|SO|PO|CLR)\/\d{4}\/\d{2}\/\d+/)?.[0];
        return operationalNumber || (value.length > 44 ? `${value.slice(0, 41)}…` : value);
    }

    statusSeverity(status: string | undefined): 'success' | 'info' | 'warn' | 'danger' | 'secondary' {
        if (status === 'BILLED' || status === 'APPROVED') return 'success';
        if (status === 'NEW' || status === 'READY' || status === 'DRAFT' || status === 'CAPTURED') return 'info';
        if (status === 'MATCH_EXCEPTION' || status === 'ON_HOLD' || status === 'APPROVAL_REQUIRED') return 'warn';
        if (status === 'CANCELLED' || status === 'REVERSED') return 'danger';
        return 'secondary';
    }

    applySavedFilter(view: { statuses: string[]; sourceTypes?: string[] }) {
        this.selectedStatuses = [...view.statuses];
        this.selectedSourceTypes = [...(view.sourceTypes ?? [])];
        this.applyFilters();
    }

    openPreview(payable: AccountPayables) {
        this.previewPayable = payable;
        this.isPreviewVisible = true;
    }

    viewPayable(payable: AccountPayables): void {
        if (payable.payableId) {
            this.router.navigate(['/accounts/enterprise/payables/to-be-billed/view', payable.payableId]);
        }
    }

    createManualPayable() {
        this.router.navigate(['/accounts/enterprise/payables/to-be-billed/manual']);
    }

    canEditPayable(payable: AccountPayables): boolean {
        return !this.isConsolidatedReadOnly
            && this.canEditPayables
            && !!payable.payableId
            && !payable.billId && !payable.billNumber
            && !['BILLED', 'CANCELLED', 'REVERSED'].includes(payable.status || '');
    }

    editPayable(payable: AccountPayables) {
        if (!this.canEditPayable(payable)) return;
        this.router.navigate(['/accounts/enterprise/payables/to-be-billed/manual', payable.payableId]);
    }

    headerSourceReferences(payable: AccountPayables): string[] {
        return distinctSourceReferences([payable.sourceReference, payable.sourceResourceNumber]);
    }

    lineSourceReferences(payable: AccountPayables): string[] {
        return distinctSourceReferences(payable.accountPayablesLines?.map(line => line.payableLineRefNumber) ?? []);
    }

    exportToExcel() {
        if (!this.canExportPayables) {
            this.messageService.add({ severity: 'warn', summary: 'Permission', detail: 'You do not have permission to export payables.' });
            return;
        }
        if (this.allPayables.length === 0) {
            this.messageService.add({ severity: 'warn', summary: 'No Data', detail: 'There is no data to export.' });
            return;
        }
        const worksheet = XLSX.utils.json_to_sheet(this.allPayables.map(p => ({
            Vendor: this.crmPartyContext.displayName(p),
            ID: p.payableId,
            Type: p.payableTxnType,
            'Ref#': p.payableReferenceNumber,
            'Header Source References': this.headerSourceReferences(p).join(', '),
            'Line Source References': this.lineSourceReferences(p).join(', '),
            Contact: this.crmPartyContext.contactDisplayName(p),
            Date: p.createdAt,
            Amount: p.amount,
            Currency: p.country?.currencyCode,
            'Bill#': p.billNumber,
            'Bill Date': p.billingDate,
            Status: p.status
        })));
        const workbook = { Sheets: { 'data': worksheet }, SheetNames: ['data'] };
        const excelBuffer: any = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        this.saveAsExcelFile(excelBuffer, "PayablesExport");
    }

    private saveAsExcelFile(buffer: any, fileName: string): void {
        const data: Blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });
        const url = window.URL.createObjectURL(data);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName + '_' + new Date().getTime() + '.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
    }

    viewDetails(payable: AccountPayables) {
        if (payable.sourceType === 'MANUAL' || String(payable.payableTxnType) === 'MANUAL') {
            this.router.navigate(['/accounts/enterprise/payables/to-be-billed/manual', payable.payableId]);
            return;
        }
        switch (payable.payableTxnType) {
            case 'SHIPMENT':
                this.router.navigate(['/shipment/view', payable.accountPayablesLines?.at(0)?.headerReferenceId]);
                break;
            case 'CLEARANCE':
                this.router.navigate(['/clearance/view', payable.accountPayablesLines?.at(0)?.headerReferenceId]);
                break;
            case 'ORDER':
                this.router.navigate(['/order/sales-order/view', payable.accountPayablesLines?.at(0)?.headerReferenceId]);
                break;
            default:
                this.messageService.add({ severity: 'warn', summary: 'Not Supported', detail: 'This receivable type is not supported for viewing details.' });
        }
    }

    editPayableLine(payableLine: AccountPayablesLine) {

    }

    generateBill() {
        if (this.checkingBillSelection || this.billSelectionBlocker) {
            this.messageService.add({ severity: 'warn', summary: 'Bill readiness',
                detail: this.billSelectionBlocker || 'Checking purchase order readiness.' });
            return;
        }
        if (this.isConsolidatedReadOnly) {
            this.messageService.add({ severity: 'warn', summary: 'Read-only scope', detail: 'Switch to a legal company to generate bills.' });
            return;
        }
        if (!this.canGenerateBill) {
            this.messageService.add({ severity: 'warn', summary: 'Permission', detail: 'You do not have permission to generate bills.' });
            return;
        }
        if (!this.selectedPayables || this.selectedPayables.length === 0) {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Please select at least one payable.' });
            return;
        }
        if (this.selectedPayables.some(value => value.status === 'BILLED')) {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'One or more selected payables are already billed.' });
            return;
        }
        this.isLoading = true;
        this.accountsService.generateBillFromAccountPayables(this.selectedPayables).subscribe({
            next: (bills) => {
                this.createdBillDrafts = bills || [];
                const draftNumbers = this.createdBillDrafts.map(draft => draft.billNumber).filter(Boolean).join(', ');
                this.messageService.add({ severity: 'success', summary: 'Draft created', detail: this.createdBillDrafts.length === 1
                    ? `Draft ${draftNumbers} created.`
                    : `${this.createdBillDrafts.length} bill drafts created${draftNumbers ? `: ${draftNumbers}` : ''}.` });
                this.selectedPayables = []; // Clear selection
                this.applyFilters(); // Refresh data to show updated statuses
                if (this.createdBillDrafts.length === 1 && this.createdBillDrafts[0].billId) {
                    this.router.navigate(['/accounts/enterprise/payables/generated-bills/view', this.createdBillDrafts[0].billId]);
                }
            },
            error: (err) => {
                this.messageService.add({ severity: 'error', summary: 'Bill Error', detail: 'Error creating bill: ' + (err.error?.message || 'Unknown error') });
                this.isLoading = false;
            }
        });
    }

    openCreatedBill(bill: Bill): void {
        if (bill.billId) this.router.navigate(['/accounts/enterprise/payables/generated-bills/view', bill.billId]);
    }

    private draftGroupKey(payable: AccountPayables): string {
        const party = payable.mainParty?.id;
        return [payable.owningCompany?.id || this.sessionStorageService.getCompany()?.id || '', party?.entityType || '', party?.entityId || '', (payable as any).currencyCode || payable.country?.currencyCode || 'SAR'].join('|');
    }

    holdSelected() {
        this.runBulkPayableAction('ON_HOLD', 'Held selected payable lines.', payable => ({
            ...payable,
            status: 'ON_HOLD',
            holdReason: payable.holdReason || 'Held from vendor billing workbench'
        }));
    }

    releaseSelected() {
        this.runBulkPayableAction('READY', 'Released selected payable lines.', payable => ({
            ...payable,
            status: 'READY',
            holdReason: undefined,
            matchExceptionReason: undefined,
            reviewReason: payable.reviewReason
        }));
    }

    submitSelectedForApproval() {
        this.runBulkPayableAction('APPROVAL_REQUIRED', 'Submitted selected manual payables for approval.', payable => ({
            ...payable,
            status: 'APPROVAL_REQUIRED',
            reviewReason: payable.reviewReason || 'Submitted for approval from vendor billing workbench'
        }));
    }

    private runBulkPayableAction(
        targetStatus: string,
        successDetail: string,
        mapper: (payable: AccountPayables) => AccountPayables
    ) {
        if (this.isConsolidatedReadOnly) {
            this.messageService.add({ severity: 'warn', summary: 'Read-only scope', detail: 'Switch to a legal company to update payables.' });
            return;
        }
        if (!this.canEditPayables) {
            this.messageService.add({ severity: 'warn', summary: 'Permission', detail: 'You do not have permission to update payables.' });
            return;
        }
        const selected = this.selectedPayables.filter(payable => !!payable.payableId && !['BILLED', 'CANCELLED', 'REVERSED'].includes(payable.status || ''));
        if (!selected.length) {
            this.messageService.add({ severity: 'warn', summary: 'No eligible rows', detail: 'Select payable lines that are not billed, cancelled, or reversed.' });
            return;
        }
        this.isLoading = true;
		forkJoin(selected.map(payable => {
			const changed = mapper(payable);
			return this.accountsService.updateAccountPayableReadiness(payable.payableId!, {
				rowVersion: payable.rowVersion,
				status: changed.status || targetStatus,
				holdReason: changed.holdReason,
				matchExceptionReason: changed.matchExceptionReason,
				reviewReason: changed.reviewReason
			});
		})).subscribe({
            next: () => {
                this.messageService.add({ severity: 'success', summary: targetStatus.replaceAll('_', ' '), detail: successDetail });
                this.selectedPayables = [];
                this.applyFilters();
            },
            error: err => {
                this.messageService.add({ severity: 'error', summary: 'Bulk update failed', detail: err?.error?.message || 'Could not update selected payables.' });
                this.isLoading = false;
            }
        });
    }

    t(key: string, params?: Record<string, unknown>): string {
        return this.localeService.translate(key, params);
    }
}
